"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export function useHorizontalSlider(totalSlides: number, initialSlide = 0) {
  const safeInitial = Math.max(
    0,
    Math.min(initialSlide, Math.max(0, totalSlides - 1)),
  );
  const [currentSlide, setCurrentSlide] = useState(safeInitial);
  const sliderRef = useRef<HTMLDivElement>(null);

  const goToSlide = useCallback(
    (slideIndex: number): void => {
      if (sliderRef.current && slideIndex >= 0 && slideIndex < totalSlides) {
        const slideWidth = sliderRef.current.offsetWidth;
        const prefersReducedMotion =
          typeof window !== "undefined" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        sliderRef.current.scrollTo({
          left: slideIndex * slideWidth,
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      }
    },
    [totalSlides],
  );

  const scrollSlider = useCallback(
    (direction: number): void => {
      let newSlide = currentSlide + direction;

      if (newSlide < 0) {
        newSlide = totalSlides - 1;
      } else if (newSlide >= totalSlides) {
        newSlide = 0;
      }

      goToSlide(newSlide);
    },
    [currentSlide, totalSlides, goToSlide],
  );

  useEffect(() => {
    const handleScroll = (): void => {
      if (sliderRef.current) {
        const slideWidth = sliderRef.current.offsetWidth;
        const scrollPosition = sliderRef.current.scrollLeft;
        const current = Math.round(scrollPosition / slideWidth);

        if (current !== currentSlide && current >= 0 && current < totalSlides) {
          setCurrentSlide(current);
        }
      }
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (slider) {
        slider.removeEventListener("scroll", handleScroll);
      }
    };
  }, [currentSlide, totalSlides]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "ArrowLeft") {
        scrollSlider(-1);
      } else if (e.key === "ArrowRight") {
        scrollSlider(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollSlider]);

  return { currentSlide, sliderRef, goToSlide, scrollSlider };
}
